import Edit from "@mui/icons-material/Edit";
import { CircularProgress, Tooltip } from "@mui/material";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useFlag } from "../../../../server/optimizely";
import { useSupabase } from "../../../../server/supabase/hooks";
import { XCircle } from "../../../../styles/icons/x-circle";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { SupabasePermissionsService } from "../../server/supabase-person-service";
import { PermissionsForm } from "./permissions-form";

import type { SupabaseCustomEmail } from "../../../channels/email/server/supabase-custom-email-service";
import type { Program } from "../../../programs/server/supabase-program-service";
import type { SupabaseSegmentWithFavourite } from "../../../segment/server/supabase-segment-service";
import type { Permissions, PersonDetails } from "../../server/supabase-person-service";
import type { PermissionsLimits } from "../../types";
import type { FC } from "react";

export const PermissionsTableActions: FC<{
  permission: Permissions;
  employees: PersonDetails[];
  capacity: PermissionsLimits;
  programs: Program[];
  segments: SupabaseSegmentWithFavourite[];
  custom_emails: SupabaseCustomEmail[];
  refresh: () => void;
}> = ({ permission, employees, capacity, programs, segments, custom_emails, refresh }) => (
  <>
    <EditAction
      permission={permission}
      employees={employees}
      capacity={capacity}
      programs={programs}
      segments={segments}
      custom_emails={custom_emails}
      onEdit={refresh}
    />
    <DeleteAction permission={permission} onDelete={refresh} />
  </>
);

const DeleteAction: FC<{ permission: Permissions; onDelete: () => void }> = ({
  permission,
  onDelete,
}) => {
  const [deleting, setDeleting] = useState(false);

  useSupabase(
    async ({ supabase }) => {
      if (!deleting || !permission.id) return;

      const { error } = await new SupabasePermissionsService(supabase).removePermissions(
        permission.id,
      );
      if (error) toast.error("Unable to delete");
      else {
        toast.success("Deleted");
        onDelete();
      }

      setDeleting(false);
    },
    [deleting, permission.id, onDelete],
  );

  return (
    <>
      <Tooltip title="Delete" placement="top" sx={{ cursor: "pointer", mr: 1 }}>
        {deleting ? (
          <CircularProgress size="small" />
        ) : (
          <XCircle
            data-testid="XCircleIcon"
            fontSize="small"
            color="info"
            onClick={() => setDeleting(true)}
          />
        )}
      </Tooltip>
    </>
  );
};

const EditAction: FC<{
  permission: Permissions;
  employees: PersonDetails[];
  capacity: PermissionsLimits;
  programs: Program[];
  segments: SupabaseSegmentWithFavourite[];
  custom_emails: SupabaseCustomEmail[];
  onEdit: () => void;
}> = ({ permission, employees, capacity, programs, segments, custom_emails, onEdit }) => {
  const { open, handleOpen, handleClose } = useDialog();
  const [reviewerRoleEnabled] = useFlag("reviewer_role");

  return (
    <>
      <Tooltip title="Edit" placement="top" sx={{ cursor: "pointer", mr: 1 }}>
        {open ? (
          <CircularProgress size="small" />
        ) : (
          <Edit fontSize="small" color="info" onClick={handleOpen} />
        )}
      </Tooltip>
      {open && (
        <PermissionsForm
          open={open}
          close={handleClose}
          employees={employees}
          capacity={capacity}
          programs={programs}
          segments={segments}
          custom_emails={custom_emails}
          existing={permission}
          showReviewerOption={reviewerRoleEnabled}
          onComplete={onEdit}
        />
      )}
    </>
  );
};
